import React from 'react';
import { useForm } from 'react-hook-form';
import { Button, Text, TextInput, YearMonthRangePopover, Icon } from 'bench-internal-theme';
import { getISODateStringInUTC, getLastDateOfMonth, toDate } from 'date-functions';
import { AppSidebar } from 'bk-web-app/app-sidebar';
import { ClientRulesSidebar } from 'bk-web-app/client-rules-sidebar';
import type { DateRange, GeneralLedgerFilters } from '../../types';
import { VisuallyHidden } from '../../NewGeneralLedger.styles';
import { useGeneralLedger } from '../../hooks/useGeneralLedger';
import {
  PageControlsLayout,
  SearchForm,
  SearchInput,
  SearchButton,
  SearchInputWrapper,
  ClearButton,
} from './PageControl.styles';
import { colors } from 'bench-style-constants';

const getFormattedDateString = dateString => {
  if (!dateString) {
    return '';
  }
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
};

type PageControlsProps = {
  dateRange: DateRange;
  filters: GeneralLedgerFilters;
  hasQualityIssues?: boolean;
  updateDateRange: (dateRange: DateRange) => any;
  updateFilters: (filters: GeneralLedgerFilters) => any;
};

type FormData = {
  search: string;
};

type PreSearchState = {
  hiddenCategories: string[];
};

const PageControls = ({
  dateRange: { endDate, startDate },
  filters: { search, showAccounts },
  updateDateRange,
  updateFilters,
}: PageControlsProps) => {
  const { updateHiddenCategories, hiddenCategories } = useGeneralLedger();
  const [preSearchState, setPreSearchState] = React.useState<PreSearchState | null>(null);
  const { register, handleSubmit, watch, reset } = useForm<FormData>();
  // If there's a start date, get the year portion, otherwise get whatever year it is locally
  const defaultDisplayedYear = startDate
    ? Number(startDate.split('-')[0])
    : new Date().getFullYear();
  // If there are start or end dates, automatically select them when the date popup opens
  const defaultStartYearMonth = startDate && startDate.substr(0, 7);
  const defaultEndYearMonth = endDate && endDate.substr(0, 7);
  // Get a formatted representation of the date range to display in the text input
  const dateRangeText =
    startDate && endDate
      ? `${getFormattedDateString(startDate)} – ${getFormattedDateString(endDate)}`
      : 'No date range selected';
  const handleRangeSelect = (startYearMonth: string | null, endYearMonth: string | null) => {
    if (!startYearMonth || !endYearMonth) return;
    updateDateRange({
      endDate: getISODateStringInUTC(getLastDateOfMonth(endYearMonth)),
      startDate: getISODateStringInUTC(toDate(startYearMonth)),
    });
  };
  const onSearch = (formData: FormData) => {
    setPreSearchState({
      hiddenCategories,
    });
    updateFilters({ search: formData.search });
    // expand any collapsed ledger categories when we search
    updateHiddenCategories && updateHiddenCategories([]);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      onClear();
    }
  };

  const onClear = () => {
    updateFilters({ search: '' });
    updateHiddenCategories && updateHiddenCategories(preSearchState?.hiddenCategories || []);
    reset();
  };

  const hasSearch = watch('search');

  return (
    <PageControlsLayout>
      <Text uppercase>Date range</Text>
      <YearMonthRangePopover
        defaultDisplayedYear={defaultDisplayedYear}
        defaultStartYearMonth={defaultStartYearMonth}
        defaultEndYearMonth={defaultEndYearMonth}
        onRangeSelect={handleRangeSelect}
      >
        {showPopover => (
          <TextInput onFocus={() => showPopover(true)} styleType="ghost" value={dateRangeText} />
        )}
      </YearMonthRangePopover>
      <SearchForm onSubmit={handleSubmit(onSearch)} name="search-form">
        <VisuallyHidden as="label" htmlFor="search">
          Search
        </VisuallyHidden>
        <SearchInputWrapper>
          <SearchInput
            {...register('search')}
            defaultValue={search}
            placeholder="Search"
            id="search"
            onKeyDown={handleInputKeyDown}
          />
          {hasSearch?.length ? (
            <ClearButton onClick={onClear} type="button">
              <Icon color={colors.white} icon="close" size="small" />
            </ClearButton>
          ) : null}
        </SearchInputWrapper>
        <SearchButton
          size="medium"
          aria-label="search"
          id="searchButton"
          icon="search"
          styleType="primary"
          type="submit"
        />
      </SearchForm>
      <AppSidebar>
        {({ closeSidebar, openSidebar }) => (
          <Button
            size="medium"
            styleType="primary"
            onClick={() => openSidebar(<ClientRulesSidebar dismiss={closeSidebar} />)}
          >
            New Rule
          </Button>
        )}
      </AppSidebar>
      <Text uppercase>Show:</Text>
      <Button
        data-heap-id="general-ledger-toggle-accounts"
        icon={showAccounts ? 'squareChecked' : 'squareOutlined'}
        onClick={() => updateFilters({ showAccounts: !showAccounts })}
        styleType="total-ghost"
      >
        Accounts
      </Button>
    </PageControlsLayout>
  );
};

PageControls.defaultProps = {
  hasQualityIssues: false,
};

export default React.memo(PageControls);
